import './ManageUsers.scss';
import { useState, useMemo, useCallback } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IoIosAdd } from 'react-icons/io';
import { SweetAlertResult } from 'sweetalert2';
import { toast } from 'react-hot-toast';
import {
  getCoreRowModel,
  useReactTable,
  PaginationState,
  ColumnDef,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  ColumnFiltersState,
  Table as ReactTable,
} from '@tanstack/react-table';
import ContentHeader from '../../components/UI/ContentHeader/ContentHeader';
import TableActions from '../../components/UI/TableActions/TableActions';
import UserService from '../../services/UserService';
import { ReactQueryKeys } from '../../constants/react-query-keys';
import SwalAlert, {
  firePreConfirmAlert,
} from '../../components/UI/SwalAlert/SwalAlert';
import Pagination from '../../components/UI/Pagination/Pagination';
import DebouncedInput from '../../components/UI/DebouncedInput/DebouncedInput';
import { User } from '../../interfaces/user';
import Select from 'react-select';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { COMPANY_ROLE_LIST, ROLES_MAPPING } from '../../constants/roles';
import ReactTooltip from 'components/UI/Tooltip/ReactTooltip';
import Table from '../../components/UI/Table/Table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectUser } from 'store/slices/auth';

const DEFAULT_PAGE_INDEX = 0;

/**
 * @description Users component that displays a table of users with filtering, sorting, and pagination within a company.
 * Allows for user management including viewing, adding, editing, and deleting users.
 *
 * @returns {JSX.Element} The ManageUsers component
 */
const ManageUsers = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);

  /**
   * @description Shows a confirmation dialog for deleting a user
   * @param {User} user - The user to be deleted
   */
  const handleTableDeleteShow = (user: User) => {
    firePreConfirmAlert({
      title: t('users.messages.modal__delete__title'),
      html: t('users.messages.modal__delete__content', {
        name: user.email,
      }),
      preConfirm: () => {
        return UserService.delete(user.id)
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      if (data.isConfirmed) {
        queryClient.invalidateQueries([ReactQueryKeys.USERS]);
        toast.success(t('users.messages.delete__success'));
      }
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMemoizedTableDeleteShow = useCallback(handleTableDeleteShow, [
    queryClient,
  ]);

  /**
   * @description Defines the columns for the table
   * @returns {ColumnDef<User>[]} An array of column definitions
   */
  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'first_name',
        header: t('users.first_name'),
        accessorKey: 'first_name',
        cell: ({ row }) => (
          <div data-testid={`first_name_${row.original.cognito_id}`}>
            {row.original.first_name ? row.original.first_name : 'N/A'}
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: 'last_name',
        accessorKey: 'last_name',
        cell: ({ row }) => (
          <div data-testid={`last_name_${row.original.cognito_id}`}>
            {row.original.last_name ? row.original.last_name : 'N/A'}
          </div>
        ),

        header: t('users.last_name'),
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: 'email',
        header: t('users.email'),
        accessorKey: 'email',
        cell: ({ row }) => (
          <div data-testid={`email_${row.original.cognito_id}`}>
            {`${row.original.email}`}
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: 'roles',
        header: t('users.access_level'),
        accessorKey: 'roles',
        cell: ({ row }) => (
          <div data-testid={`roles_${row.original.cognito_id}`}>
            {`${t(ROLES_MAPPING[row.original.roles])}`}
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: true,
      },

      {
        id: 'phone_number',
        accessorKey: 'phone_number',
        header: t('users.phone_number'),
        enableSorting: true,
        enableColumnFilter: true,
        cell: ({ row }) => (
          <div className="table-actions-wrapper">
            <div data-testid={`phone_number_${row.original.cognito_id}`}>
              {row.original.phone_number
                ? formatPhoneNumberIntl(row.original.phone_number)
                : 'N/A'}
            </div>
            <TableActions
              editLink={`/settings/users/${row.original.id}`}
              onDelete={
                row.original.cognito_id !== user?.cognito_id
                  ? () => {
                      handleMemoizedTableDeleteShow(row.original);
                    }
                  : null
              }
            />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleMemoizedTableDeleteShow],
  );

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const dataQuery = useQuery(
    [
      ReactQueryKeys.USERS,
      {
        pageIndex,
        pageSize,
        sorting,
        columnFilters,
      },
    ],
    () =>
      UserService.get({
        pageIndex,
        pageSize,
        sorting,
        columnFilters,
      }),
    { keepPreviousData: true },
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  /**
   * Define react table
   */
  const table: ReactTable<User> = useReactTable({
    data: dataQuery.data?.data.records ?? [],
    columns,
    pageCount: dataQuery?.data?.data.totalPages ?? -1,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: (value) => {
      setColumnFilters(value);
      setPagination((oldValue) => ({
        ...oldValue,
        pageIndex: DEFAULT_PAGE_INDEX,
      }));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: false,
  });

  return (
    <>
      <ContentHeader
        title={t('users.title')}
        actions={
          <div className="d-flex">
            <span
              data-tooltip-id="content-header-button"
              data-tooltip-content={t('buttons.add')}
            >
              <Link
                to="/settings/users/add"
                className="btn-rounded"
                data-testid="add-btn"
              >
                <IoIosAdd size={40} />
              </Link>
            </span>
            <ReactTooltip
              id="content-header-button"
              place="bottom"
              style={{ fontSize: '13px' }}
            />
          </div>
        }
      />

      <Table
        classes="manage-users-table"
        isFetching={dataQuery.isFetching}
        table={table}
        filters={(header) => {
          return (
            <>
              {/* Roles filter  */}
              {header.column.id === 'roles' && (
                <Select
                  id="roleSelect"
                  aria-label="roles"
                  maxMenuHeight={300}
                  onChange={header.column.setFilterValue}
                  value={header.column.getFilterValue()}
                  isMulti={false}
                  className="react-select-container-sm"
                  classNamePrefix="react-select"
                  isClearable
                  isSearchable
                  options={COMPANY_ROLE_LIST.map((role) => ({
                    label: t(ROLES_MAPPING[role]),
                    value: role,
                  }))}
                />
              )}

              {!['roles'].includes(header.column.id) && (
                <DebouncedInput
                  value={(header.column.getFilterValue() ?? '') as string}
                  type="text"
                  onChange={(value) => header.column.setFilterValue(value)}
                  className="form-control form-control-sm rounded"
                  data-testid={`filter_input_${header.column.id}`}
                />
              )}
            </>
          );
        }}
      />
      <Pagination table={table} />
      <Outlet />
    </>
  );
};

export default ManageUsers;
